import React, { useEffect, useState, useCallback, useRef } from "react";
import config, { isDefaultCluster } from "../Config";
import { useNavigate } from "react-router-dom";
import { HandleResponse } from "../functions/HandleResponse";
import { authFetch } from "../functions/authFetch";
import { downloadJobLog, formatLogSize } from "../functions/jobLog";
import "./pages.css";

// Terminal statuses (a job in any of these can no longer be deleted).
const TERMINATED = ["Completed", "Failed", "OOMKilled", "Error", "ContainerCannotRun", "DeadlineExceeded", "Evicted", "Killed", "CrashLoopBackOff", "Deleted"];

const isDeletable = (status) => !!status && !TERMINATED.includes(status) && status !== "deleting";

const statusClass = (status) => {
  if (status === "Completed") return "ok";
  if (TERMINATED.includes(status)) return "fail";
  return "pending"; // running / created / deleting
};

const JOB_TYPES = [
  { id: "mcr", label: "MCR", desc: "Mermadic Container Registry — no credentials needed" },
  { id: "dockerhub", label: "DockerHub", desc: "Public or private Docker Hub image" },
  { id: "ecr", label: "ECR", desc: "AWS Elastic Container Registry" },
  { id: "acr", label: "ACR", desc: "Azure Container Registry" },
];

const ENDPOINTS = {
  mcr: "/v1/mcr/create-job",
  dockerhub: "/v1/dockerhub/create-job",
  ecr: "/v1/ecr/create-job",
  acr: "/v1/acr/create-job",
};

const emptyKV = () => ({ key: "", value: "" });

// Placeholder key shown in the sample curl — never a real credential.
const MOCK_API_KEY = "mmc_sk_1a2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p";

// Map a job's stored image source to its create-job endpoint.
const SOURCE_ENDPOINTS = {
  mcr: "/v1/mcr/create-job",
  dockerhub: "/v1/dockerhub/create-job",
  ecr: "/v1/ecr/create-job",
  acr: "/v1/acr/create-job",
};

// Parse the stored "cpu=0.5 memory=0.25" resource string into a payload object.
const parseResourceString = (s) => {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/cpu=([\d.]+)\s+memory=([\d.]+)/);
  if (!m) return null;
  return { cpu: parseFloat(m[1]), memory: parseFloat(m[2]) };
};

const fmtTime = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch { return String(v); }
};

const relTime = (v) => {
  if (!v) return "";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    let sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 0) sec = 0;
    const units = [["y", 31536000], ["mo", 2592000], ["d", 86400], ["h", 3600], ["m", 60]];
    for (const [label, s] of units) {
      const val = Math.floor(sec / s);
      if (val >= 1) return `${val}${label} ago`;
    }
    return `${sec}s ago`;
  } catch { return ""; }
};

const Log = () => {
  const navigate = useNavigate();
  const base = config.RUNNER_API;
  const isDedicated = !isDefaultCluster();

  // ── job list ──
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // ── selection / detail ──
  const [selectedJob, setSelectedJob] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── delete ──
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── sample curl copy feedback ──
  const [copied, setCopied] = useState(false);

  // ── full-log download ──
  const [downloading, setDownloading] = useState(false);

  const [banner, setBanner] = useState(null);

  // ── create modal ──
  const [modalOpen, setModalOpen] = useState(false);
  const [jobType, setJobType] = useState("mcr");
  const [image, setImage] = useState("");
  const [useCommandArgs, setUseCommandArgs] = useState(false);
  const [cmdArgs, setCmdArgs] = useState([emptyKV()]);
  const [useResources, setUseResources] = useState(false);
  const [cpu, setCpu] = useState("0.5");
  const [memory, setMemory] = useState("0.25");
  const [useGpu, setUseGpu] = useState(false);
  const [useEnv, setUseEnv] = useState(false);
  const [envVars, setEnvVars] = useState([emptyKV()]);
  const [useDockerCred, setUseDockerCred] = useState(false);
  const [dockerUser, setDockerUser] = useState("");
  const [dockerPass, setDockerPass] = useState("");
  const [awsKey, setAwsKey] = useState("");
  const [awsSecret, setAwsSecret] = useState("");
  const [awsRegion, setAwsRegion] = useState("us-east-1");
  const [azureUser, setAzureUser] = useState("");
  const [azurePass, setAzurePass] = useState("");
  const [mcrImages, setMcrImages] = useState([]);
  const [mcrLoading, setMcrLoading] = useState(false);
  const [useConfigMap, setUseConfigMap] = useState(false);
  const [configMapName, setConfigMapName] = useState("");
  const [configMaps, setConfigMaps] = useState([]);
  const [configMapsLoading, setConfigMapsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // GPU jobs run on Premium clusters only, so the toggle is hidden elsewhere and
  // useGpu is never sent from those tiers.
  const [clusterTier, setClusterTier] = useState(null);
  const gpuAvailable = clusterTier === "prod";

  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedJob?.JobID || null;

  // ── data loading ────────────────────────────────────────────────────────
  const fetchJobs = useCallback(async (silent = false) => {
    if (!silent) setJobsLoading(true);
    try {
      const res = await authFetch(`${base}/v1/all-logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recentMonths: 3, showLimit: 200, terminationStatus: statusFilter }),
      });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const data = await res.json();
        setJobs(Array.isArray(data.logs) ? data.logs : []);
      }
    } catch { /* ignore */ }
    finally { if (!silent) setJobsLoading(false); }
  }, [base, navigate, statusFilter]);

  const loadDetail = useCallback(async (jobId, silent = false) => {
    if (!silent) { setDetailLoading(true); setDetail(null); }
    try {
      const res = await authFetch(`${base}/v1/get-job-detail?job_id=${encodeURIComponent(jobId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "",
      });
      if (res.status === 403) { navigate("/login"); return; }
      if (res.ok) {
        const data = await res.json();
        if (selectedIdRef.current === jobId) setDetail(data);
      }
    } catch { /* ignore */ }
    finally { if (!silent) setDetailLoading(false); }
  }, [base, navigate]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  useEffect(() => {
    authFetch(`${base}/v1/deployment/get-cluster-tier`, { method: "GET" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.tier) setClusterTier(d.tier); })
      .catch(() => {});
  }, [base]);

  // Light auto-refresh of the list and the selected (still-running) job.
  useEffect(() => {
    const t = setInterval(() => {
      if (modalOpen) return;
      fetchJobs(true);
      const id = selectedIdRef.current;
      if (id) loadDetail(id, true);
    }, 15000);
    return () => clearInterval(t);
  }, [modalOpen, fetchJobs, loadDetail]);

  const selectJob = (job) => {
    setSelectedJob(job);
    setDetail(null);
    loadDetail(job.JobID);
  };

  const currentStatus = (detail && detail.Status) || selectedJob?.Status;

  const logLines = (() => {
    if (!detail) return null;
    const logStr = (detail.JobLog || "").replace(/\\n/g, "\n");
    const lines = logStr.split("\n").map((l) => l.replace(/^"|"$/g, ""));
    return ["─── Output ───", ...lines];
  })();

  const curlText = (() => {
    if (!selectedJob) return "";
    const source = (detail?.ImageSource || "mcr").toLowerCase();
    const endpoint = SOURCE_ENDPOINTS[source] || SOURCE_ENDPOINTS.mcr;
    const body = { image: detail?.image || selectedJob.image };
    if (Array.isArray(detail?.command_args) && detail.command_args.length) {
      body.commandArgs = detail.command_args;
    }
    const rr = parseResourceString(detail?.ResourceRequested);
    if (rr) body.resourceRequests = rr;
    if (detail?.useGpu) body.useGpu = true;
    if (detail?.configmapName) body.configmapName = detail.configmapName;
    if (source === "ecr") {
      body.credential = { awsAccessKeyId: "YOUR_AWS_ACCESS_KEY_ID", awsSecretAccessKey: "YOUR_AWS_SECRET", regionName: "us-east-1" };
    } else if (source === "acr") {
      body.credential = { azureUsername: "YOUR_AZURE_USERNAME", azurePassword: "YOUR_AZURE_PASSWORD" };
    }
    return `curl -X POST "${base}${endpoint}" \\\n  -H "Authorization: Bearer ${MOCK_API_KEY}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 2)}'`;
  })();

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(curlText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const downloadLog = async () => {
    if (!selectedJob || downloading) return;
    setDownloading(true);
    try {
      await downloadJobLog(selectedJob.JobID);
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Could not download the log." });
    } finally {
      setDownloading(false);
    }
  };

  // ── delete ──────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!selectedJob || deleting) return;
    const jobId = selectedJob.JobID;
    setDeleting(true);
    try {
      const res = await authFetch(`${base}/v1/delete-job?job_id=${encodeURIComponent(jobId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "",
      });
      await HandleResponse(res, navigate);
      if (res.ok) {
        setConfirmOpen(false);
        setBanner({ type: "success", text: `Job ${jobId} is being deleted.` });
        await fetchJobs(true);
        loadDetail(jobId, true);
      } else {
        const data = await res.json().catch(() => ({}));
        setBanner({ type: "error", text: data.detail || "Failed to delete job." });
      }
    } catch {
      setBanner({ type: "error", text: "Failed to delete job." });
    } finally {
      setDeleting(false);
    }
  };

  // ── create modal ────────────────────────────────────────────────────────
  const fetchMcrImages = useCallback(async () => {
    setMcrLoading(true);
    try {
      const res = await authFetch(`${base}/v1/cdci/mcr/images?image_type=short`, { method: "GET" });
      if (res.ok) {
        const data = await res.json();
        setMcrImages(Array.isArray(data) ? data.map((i) => i.image_name || i) : []);
      }
    } catch { /* ignore */ }
    finally { setMcrLoading(false); }
  }, [base]);

  const fetchConfigMaps = useCallback(async () => {
    setConfigMapsLoading(true);
    try {
      const res = await authFetch(`${base}/v1/deployment/list-configmap`, { method: "GET" });
      if (res.ok) {
        const data = await res.json();
        setConfigMaps(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ }
    finally { setConfigMapsLoading(false); }
  }, [base]);

  useEffect(() => {
    if (modalOpen && jobType === "mcr") fetchMcrImages();
  }, [modalOpen, jobType, fetchMcrImages]);

  useEffect(() => {
    if (modalOpen && useConfigMap && configMaps.length === 0) fetchConfigMaps();
  }, [modalOpen, useConfigMap, fetchConfigMaps, configMaps.length]);

  const openCreate = () => {
    setJobType("mcr");
    setImage("");
    setUseCommandArgs(false); setCmdArgs([emptyKV()]);
    setUseResources(false); setCpu("0.5"); setMemory("0.25");
    setUseGpu(false);
    setUseEnv(false); setEnvVars([emptyKV()]);
    setUseDockerCred(false); setDockerUser(""); setDockerPass("");
    setAwsKey(""); setAwsSecret(""); setAwsRegion("us-east-1");
    setAzureUser(""); setAzurePass("");
    setUseConfigMap(false); setConfigMapName("");
    setFormError(null);
    setModalOpen(true);
  };

  const updateRow = (setter) => (idx, field, val) =>
    setter((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  const addRow = (setter) => () => setter((prev) => [...prev, emptyKV()]);
  const removeRow = (setter) => (idx) => setter((prev) => prev.filter((_, i) => i !== idx));

  const updateCmd = updateRow(setCmdArgs);
  const addCmd = addRow(setCmdArgs);
  const removeCmd = removeRow(setCmdArgs);
  const updateEnv = updateRow(setEnvVars);
  const addEnv = addRow(setEnvVars);
  const removeEnv = removeRow(setEnvVars);

  const buildPayload = () => {
    const body = { image };
    if (useCommandArgs) {
      const args = [];
      for (const row of cmdArgs) {
        const k = row.key.trim();
        const v = row.value.trim();
        if (!k) continue;
        args.push(`--${k.replace(/^-+/, "")}`);
        if (v) args.push(v);
      }
      if (args.length) body.commandArgs = args;
    }
    if (useResources) {
      body.resourceRequests = { cpu: parseFloat(cpu) || 0.5, memory: parseFloat(memory) || 0.25 };
    }
    if (gpuAvailable && useGpu) body.useGpu = true;
    if (useEnv) {
      const pairs = envVars.filter((r) => r.key.trim()).map((r) => ({ name: r.key.trim(), value: r.value }));
      if (pairs.length) body.env = pairs;
    }
    if (useConfigMap && configMapName.trim()) body.configmapName = configMapName.trim();
    if (jobType === "dockerhub" && useDockerCred && dockerUser.trim() && dockerPass.trim()) {
      body.credential = { dockerhubUsername: dockerUser.trim(), dockerhubPassword: dockerPass.trim() };
    }
    if (jobType === "ecr") {
      body.credential = { awsAccessKeyId: awsKey.trim(), awsSecretAccessKey: awsSecret.trim(), regionName: awsRegion.trim() || "us-east-1" };
    }
    if (jobType === "acr") {
      body.credential = { azureUsername: azureUser.trim(), azurePassword: azurePass.trim() };
    }
    return body;
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!image.trim()) { setFormError("Image is required."); return; }
    if (jobType === "ecr" && (!awsKey.trim() || !awsSecret.trim())) { setFormError("AWS credentials are required for ECR jobs."); return; }
    if (jobType === "acr" && (!azureUser.trim() || !azurePass.trim())) { setFormError("Azure credentials are required for ACR jobs."); return; }

    setSubmitting(true);
    try {
      const res = await authFetch(`${base}${ENDPOINTS[jobType]}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const jobId = (await res.text().catch(() => "")).replace(/"/g, "");
        setModalOpen(false);
        setBanner({ type: "success", text: `Job created${jobId ? ` · ${jobId}` : ""}.` });
        await fetchJobs(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setFormError(data.detail || `Job creation failed (${res.status}).`);
      }
    } catch (err) {
      setFormError(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderKVList = (rows, enabled, updateFn, addFn, removeFn, keyPlaceholder, valuePlaceholder) => (
    <div className={`cj-kv-list ${!enabled ? "cj-disabled" : ""}`}>
      {rows.map((row, i) => (
        <div key={i} className="cj-kv-row">
          <input className="cj-input cj-kv-key" placeholder={keyPlaceholder} value={row.key}
            onChange={(e) => updateFn(i, "key", e.target.value)} disabled={!enabled} autoComplete="off" />
          <input className="cj-input cj-kv-val" placeholder={valuePlaceholder} value={row.value}
            onChange={(e) => updateFn(i, "value", e.target.value)} disabled={!enabled} autoComplete="off" />
          {enabled && <button type="button" className="cj-kv-remove" onClick={() => removeFn(i)} title="Remove">×</button>}
        </div>
      ))}
      {enabled && <button type="button" className="cj-kv-add" onClick={addFn}>+ Add</button>}
    </div>
  );

  return (
    <div className="cron-page">
      {banner && (
        <div className={`cron-banner cron-banner--${banner.type}`} onAnimationEnd={() => setBanner(null)}>
          {banner.text}
        </div>
      )}

      {/* ── Top toolbar ── */}
      <header className="cron-topbar">
        <div className="cron-topbar-left">
          <h1 className="cron-h1">Jobs</h1>
          <p className="cron-sub">
            Run and monitor one-off container jobs
            <span className={`cron-cluster-badge ${isDedicated ? "dedicated" : "shared"}`}>
              {isDedicated ? "Dedicated cluster" : "Shared cluster"}
            </span>
          </p>
        </div>
        <button className="cron-btn-primary cron-btn-lg" onClick={openCreate}>
          <span className="cron-plus">+</span> New Job
        </button>
      </header>

      <div className="cron-grid">
        {/* ── Pane 1: job list ── */}
        <section className="cron-pane cron-pane--list">
          <div className="cron-pane-head">
            <span className="cron-pane-title">Jobs</span>
            <div className="cron-actions">
              <select className="jm-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="running">Running</option>
                <option value="terminated">Terminated</option>
              </select>
              <button className="cron-chip-btn" onClick={() => fetchJobs()} title="Refresh">⟳</button>
            </div>
          </div>
          <div className="cron-scroll">
            {jobsLoading && <div className="cron-empty">Loading…</div>}
            {!jobsLoading && jobs.length === 0 && (
              <div className="cron-empty cron-empty--big">
                <div className="cron-empty-icon">📦</div>
                <div className="cron-empty-title">No jobs yet</div>
                <div className="cron-empty-text">Create one to run a container as a job.</div>
                <button className="cron-btn-primary" onClick={openCreate}>+ New Job</button>
              </div>
            )}
            {jobs.map((j) => (
              <button
                key={j.JobID}
                type="button"
                className={`cron-card ${selectedJob?.JobID === j.JobID ? "active" : ""}`}
                onClick={() => selectJob(j)}
              >
                <div className="cron-card-top">
                  <span className="cron-card-name jm-mono" title={j.JobID}>{j.JobID}</span>
                  <span className={`cron-badge ${statusClass(j.Status)}`}>{j.Status || "pending"}</span>
                </div>
                <div className="cron-card-bottom">
                  <span className="cron-card-image" title={j.image}>{j.image || "—"}</span>
                  <span className="cron-run-rel">{relTime(j.StartTime)}</span>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* ── Pane 2: status / detail / delete ── */}
        <section className="cron-pane cron-pane--exec">
          {!selectedJob ? (
            <div className="cron-empty cron-empty--big cron-empty--center">
              <div className="cron-empty-icon">🗂</div>
              <div className="cron-empty-title">Select a job</div>
              <div className="cron-empty-text">Its status and details will appear here.</div>
            </div>
          ) : (
            <>
              <div className="cron-pane-head">
                <span className="cron-pane-title jm-mono" title={selectedJob.JobID}>{selectedJob.JobID}</span>
                {isDeletable(currentStatus) && (
                  <div className="cron-actions">
                    <button className="cron-chip-btn danger" onClick={() => setConfirmOpen(true)} disabled={deleting}>
                      {deleting ? "Deleting…" : "Delete"}
                    </button>
                  </div>
                )}
              </div>

              <div className="jm-status-row">
                <span className="cron-stat-label">Status</span>
                <span className={`cron-badge ${statusClass(currentStatus)}`}>{currentStatus || "pending"}</span>
              </div>

              <div className="cron-stats">
                <div className="cron-stat">
                  <span className="cron-stat-label">Image</span>
                  <span className="cron-stat-value cron-stat-mono" title={selectedJob.image}>{selectedJob.image || "—"}</span>
                </div>
                <div className="cron-stat">
                  <span className="cron-stat-label">Started</span>
                  <span className="cron-stat-value">{fmtTime(detail?.StartTime || selectedJob.StartTime)}</span>
                </div>
                <div className="cron-stat">
                  <span className="cron-stat-label">Finished</span>
                  <span className="cron-stat-value">{detail && detail.FinishedTime && detail.FinishedTime !== "TBD" ? fmtTime(detail.FinishedTime) : "—"}</span>
                </div>
                <div className="cron-stat">
                  <span className="cron-stat-label">Resources</span>
                  <span className="cron-stat-value">
                    {detail?.ResourceRequested || "—"}
                    {detail?.useGpu ? " · GPU" : ""}
                  </span>
                </div>
                <div className="cron-stat cron-stat--wide">
                  <span className="cron-stat-label">Command</span>
                  <span className="cron-stat-value cron-stat-mono">{Array.isArray(detail?.command_args) && detail.command_args.length ? detail.command_args.join(" ") : "—"}</span>
                </div>
              </div>

              <div className="jm-hint">
                <div className="jm-hint-head">
                  <span className="jm-hint-label">💡 Create a job like this via the API</span>
                  <button type="button" className="jm-hint-copy" onClick={copyCurl}>
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <pre className="jm-hint-code"><code>{curlText}</code></pre>
                <span className="jm-hint-note">Sample API key shown — generate your own under <strong>My Account → Developer Access</strong>.</span>
              </div>

              {detailLoading && <div className="cron-empty">Loading details…</div>}
            </>
          )}
        </section>

        {/* ── Pane 3: log ── */}
        <section className="cron-pane cron-pane--log">
          <div className="cron-log-head">
            <div className="cron-log-head-info">
              <span className="cron-log-icon" aria-hidden="true">❯_</span>
              <span className="cron-log-label">Job Log</span>
              {selectedJob && <span className="cron-log-title">{selectedJob.JobID}</span>}
              {selectedJob && logLines && (
                <span className="cron-log-count">{logLines.length} lines</span>
              )}
            </div>
            {selectedJob && logLines && (
              <button
                className="jm-download-btn"
                onClick={downloadLog}
                disabled={downloading}
                title="Download the full log as a .log file"
              >
                {downloading ? "Downloading…" : "⬇ Download Log"}
              </button>
            )}
          </div>
          <div className="cron-log-body">
            {detailLoading && !logLines ? (
              <span className="cron-log-placeholder">Loading…</span>
            ) : logLines ? (
              <>
                {detail?.LogTruncated && (
                  <div className="jm-log-truncated">
                    This log is {formatLogSize(detail.LogTotalBytes)} — too large to show in full,
                    so only the end is displayed. <strong>Download Log</strong> to view all of it.
                  </div>
                )}
                {logLines.map((line, i) => (
                  <div key={i} className={`cron-log-line ${line.startsWith("─") ? "sep" : ""}`}>{line || "\u00A0"}</div>
                ))}
              </>
            ) : (
              <div className="cron-log-empty">
                <div className="cron-log-empty-icon">📄</div>
                <span>Select a job to view its log</span>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ── Create job modal ── */}
      {modalOpen && (
        <div className="cron-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div className="cron-modal" role="dialog" aria-modal="true">
            <div className="cron-modal-header">
              <span>New Job</span>
              <button className="cron-modal-close" onClick={() => setModalOpen(false)} aria-label="Close">×</button>
            </div>
            <form className="cron-form jm-create-form" onSubmit={submitCreate} autoComplete="off">
              {/* image source */}
              <div className="cron-field">
                <label className="cron-label">Image Source</label>
                <div className="cj-type-bar">
                  {JOB_TYPES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`cj-type-btn ${jobType === t.id ? "active" : ""}`}
                      onClick={() => { setJobType(t.id); setImage(""); setFormError(null); }}
                    >
                      <span className="cj-type-label">{t.label}</span>
                      <span className="cj-type-desc">{t.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* image */}
              <div className="cron-field">
                <label className="cron-label">Image {jobType === "mcr" && mcrLoading && <span className="cron-hint">refreshing…</span>}</label>
                {jobType === "mcr" ? (
                  <select className="cron-input" value={image} onChange={(e) => setImage(e.target.value)} onMouseDown={fetchMcrImages} onFocus={fetchMcrImages}>
                    <option value="">— select an MCR image —</option>
                    {image && !mcrLoading && !mcrImages.includes(image) && <option value={image}>{image} — no longer in registry</option>}
                    {mcrImages.map((img) => <option key={img} value={img}>{img}</option>)}
                  </select>
                ) : (
                  <input
                    className="cron-input"
                    value={image}
                    onChange={(e) => setImage(e.target.value)}
                    autoComplete="off"
                    placeholder={
                      jobType === "dockerhub" ? "e.g. python:3.11-slim" :
                      jobType === "ecr" ? "e.g. 123456789.dkr.ecr.us-east-1.amazonaws.com/my-repo:latest" :
                      "e.g. myregistry.azurecr.io/my-image:latest"
                    }
                  />
                )}
              </div>

              {/* credentials */}
              {jobType === "dockerhub" && (
                <div className="cron-field">
                  <label className="cron-toggle">
                    <input type="checkbox" checked={useDockerCred} onChange={(e) => setUseDockerCred(e.target.checked)} />
                    Credentials (private image)
                  </label>
                  <div className={`cj-cred-grid ${!useDockerCred ? "cj-disabled" : ""}`}>
                    <input className="cron-input" placeholder="Docker Hub username" value={dockerUser} onChange={(e) => setDockerUser(e.target.value)} disabled={!useDockerCred} autoComplete="off" data-1p-ignore="true" />
                    <input className="cron-input" type="password" placeholder="Docker Hub password" value={dockerPass} onChange={(e) => setDockerPass(e.target.value)} disabled={!useDockerCred} autoComplete="new-password" data-1p-ignore="true" />
                  </div>
                </div>
              )}

              {jobType === "ecr" && (
                <div className="cron-field">
                  <label className="cron-label">AWS Credentials <span className="cron-hint">(required)</span></label>
                  <div className="cj-cred-grid">
                    <input className="cron-input" placeholder="AWS Access Key ID" value={awsKey} onChange={(e) => setAwsKey(e.target.value)} autoComplete="off" data-1p-ignore="true" />
                    <input className="cron-input" type="password" placeholder="AWS Secret Access Key" value={awsSecret} onChange={(e) => setAwsSecret(e.target.value)} autoComplete="new-password" data-1p-ignore="true" />
                    <input className="cron-input" placeholder="Region (e.g. us-east-1)" value={awsRegion} onChange={(e) => setAwsRegion(e.target.value)} autoComplete="off" data-1p-ignore="true" />
                  </div>
                </div>
              )}

              {jobType === "acr" && (
                <div className="cron-field">
                  <label className="cron-label">Azure Credentials <span className="cron-hint">(required)</span></label>
                  <div className="cj-cred-grid">
                    <input className="cron-input" placeholder="Azure username" value={azureUser} onChange={(e) => setAzureUser(e.target.value)} autoComplete="off" data-1p-ignore="true" />
                    <input className="cron-input" type="password" placeholder="Azure password" value={azurePass} onChange={(e) => setAzurePass(e.target.value)} autoComplete="new-password" data-1p-ignore="true" />
                  </div>
                </div>
              )}

              {/* command args */}
              <div className="cron-field">
                <label className="cron-toggle">
                  <input type="checkbox" checked={useCommandArgs} onChange={(e) => setUseCommandArgs(e.target.checked)} />
                  Command Arguments <span className="cron-hint">(flag name → value)</span>
                </label>
                {renderKVList(cmdArgs, useCommandArgs, updateCmd, addCmd, removeCmd, "flag name", "value")}
              </div>

              {/* resources */}
              <div className="cron-field">
                <label className="cron-toggle">
                  <input type="checkbox" checked={useResources} onChange={(e) => setUseResources(e.target.checked)} />
                  Resource Requests
                </label>
                {useResources && (
                  <div className="cron-field-2col">
                    <div className="cron-field">
                      <label className="cron-label-sm">CPU (vCPU)</label>
                      <input className="cron-input" type="number" step="0.1" min="0.1" value={cpu} onChange={(e) => setCpu(e.target.value)} />
                    </div>
                    <div className="cron-field">
                      <label className="cron-label-sm">Memory (GB)</label>
                      <input className="cron-input" type="number" step="0.25" min="0.25" value={memory} onChange={(e) => setMemory(e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              {/* gpu — Premium only */}
              {gpuAvailable && (
                <div className="cron-field">
                  <label className="cron-toggle">
                    <input type="checkbox" checked={useGpu} onChange={(e) => setUseGpu(e.target.checked)} />
                    Use GPU <span className="cron-hint">(1&times; NVIDIA T4)</span>
                  </label>
                  {useGpu && (
                    <span className="cron-hint">
                      Billed per GPU-hour (15-minute minimum) on top of CPU and memory usage.
                      Capped at 3 vCPU / 12 GB; first start may take a few minutes while a GPU
                      node is provisioned.
                    </span>
                  )}
                </div>
              )}

              {/* env */}
              <div className="cron-field">
                <label className="cron-toggle">
                  <input type="checkbox" checked={useEnv} onChange={(e) => setUseEnv(e.target.checked)} />
                  Environment Variables
                </label>
                {renderKVList(envVars, useEnv, updateEnv, addEnv, removeEnv, "NAME", "value")}
              </div>

              {/* configmap */}
              <div className="cron-field">
                <label className="cron-toggle">
                  <input type="checkbox" checked={useConfigMap} onChange={(e) => setUseConfigMap(e.target.checked)} />
                  ConfigMap {!isDedicated && <span className="cron-hint">(stored as env file on shared)</span>}
                </label>
                {useConfigMap && (
                  <select className="cron-input" value={configMapName} onChange={(e) => setConfigMapName(e.target.value)}>
                    <option value="">{configMapsLoading ? "Loading…" : configMaps.length === 0 ? "— no ConfigMaps found —" : "— select a ConfigMap —"}</option>
                    {configMaps.map((cm) => <option key={cm} value={cm}>{cm}</option>)}
                  </select>
                )}
              </div>

              {formError && <p className="cron-form-error">{formError}</p>}

              <div className="cron-modal-actions">
                <button type="button" className="cron-btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="cron-btn-primary" disabled={submitting}>
                  {submitting ? "Creating…" : "Create Job"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete confirmation modal ── */}
      {confirmOpen && (
        <div className="cl-modal-overlay" onClick={() => { if (!deleting) setConfirmOpen(false); }}>
          <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
            <p className="cl-modal-title">// confirm delete</p>
            <p className="cl-modal-body">
              This running job's container will be stopped and billing finalized. The run will be
              marked as <strong>Deleted</strong>. This cannot be undone.
            </p>
            <p className="cl-modal-cluster"><code>{selectedJob?.JobID}</code></p>
            <div className="cl-modal-actions">
              <button className="cl-modal-confirm" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Processing…" : "Confirm Delete"}
              </button>
              <button className="cl-modal-cancel" onClick={() => setConfirmOpen(false)} disabled={deleting}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Log;
